import { getModel } from '../../model/registry'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useParams, useSearchParams, useLocation } from 'react-router-dom'
import generateEndpoint from '../../utils/generateEndpoint'
import useMobileNavBar from '../../hooks/useMobileNavBar'

import transferSettings, { getSettingsKey } from '../pageView/processSettings'

//setup using the API
import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import useApi from "../../hooks/useApi"
import useStaleTime from '../../hooks/useStaleTime'
import useParent from '../../hooks/useParent'
//Importing the Subcomponents
import PageLayout from './PageLayout'
import TransitionLayer from './layers/TransitionLayer'
import runFieldsLogic from '../../../../common/logic/runFieldsLogic'

import Loader from '../generic/Loader'

import { useStore } from './Page'


const PageView = ({ type, options, dispatch }) => {
  //Config to read the data from the Model
  let config = getModel('pageViewData')[type]
  //console.log('[PageView] config: ', config)
  if (!config) return <span>Not a config found for {type}</span>

  // This is an option on routes to reload the data if it changes regardless it if has already loaded.
  const dynamicReload = options.dynamicReload || false

  //Setting up the state for the form.
  //const [loaded, setState] = useStore((s) => s['loaded'])
  const [state, setState, getState] = useStore((s) => s)
  const loaded = state.loaded
  // Track the last dataUpdatedAt timestamp we processed to avoid re-processing
  // the same fetch result (prevents PDF re-render issues on background refetches)
  const lastProcessedRef = useRef(null)
  const { hideMobileNav, showMobileNav, mobileNavBarState } = useMobileNavBar()

  const { getMe } = useApi()
  const staleTime = useStaleTime(config.entity)
  const queryClient = useQueryClient()
  const getSettingsData = (key) => queryClient.getQueryData([key])?.data

  //Getting the option params
  const mode = options.mode
  const load = options.load

  //Id of the entity shown taken from the URL. Collects both id and id2 if it exists
  const { id, id2 } = useParams()
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Retrieve the value of the 'state' parameter and store it in the sliug variable
  const slug = searchParams.get('state');

  //this formats the endpoint to include the first id in the main route in case of a subroute
  let endpoint;
  if (config && config.path !== undefined) {
    endpoint = generateEndpoint(config.path, id);
  }

  //this assigns id2 if that exists otherwise it is id
  let itemId = id2 ?? id
  if (options.forcedId) itemId = options.forcedId
  if (options.forcedPath) endpoint = options.forcedPath

  //This is used in transitions to add Fields
  if (config?.transition?.active) {
    if (!options.createFromEntity) options.createFromEntity = config.entity
    options.createFromId = itemId
  }

  // Fetch parent and map fields defined in entities[childEntity].transferData into the current form
  const parent = useParent(config?.path, id);

  //This ensures that whatever the parent has passed on is triggering the fieldsLogic
  //Note it requires we add: fieldsLogic: ['currency'] to the config of pageViewData
  //
  //Both the read and the write are deliberate, and both used to be wrong.
  //
  //This runs when the parent fetch resolves — an unknown time after the render
  //that scheduled it, with an awaited runFieldsLogic on top. A closed-over
  //`state` is therefore the form as it was *before* the user could type into
  //it, and writing that whole object back reverts everything typed since. It
  //cost the `O` in a BOM version called BOM-1: the record saved as BM-1, no
  //error, no failed request. Every new subcollection card had it — bomVersion,
  //itemPrice, supplierItem, contactPerson.
  //
  //So: read through the store's getter at call time, and write back only the
  //keys this actually changed. Everything else is left exactly as the user has
  //it now, rather than as it was when the fetch started.
  const applyFieldsLogic = async () => {
      const before = getState()
      const processedData = await runFieldsLogic({ ...before, ...parent.data }, before.entity, config.fieldsLogic, before)

      const patch = {}
      for (const key of Object.keys(processedData)) {
          if (!Object.is(processedData[key], before[key])) patch[key] = processedData[key]
      }

      if (Object.keys(patch).length > 0) setState(patch)
  }

  // Merge transferred fields into form state once the main record is initialized
  useEffect(() => {
    if (parent.success && parent.data && loaded) {
      //setState(parent.data);
      applyFieldsLogic()
    }
  }, [parent.success, parent.data, loaded, setState]);

  // Subscribe to settings queries so we wait for them before processing the page data.
  // Layout.jsx prefetches these as an optimisation, but on a hard reload the cache is
  // empty when the main record arrives. useQueries guarantees they are fetched and ready
  // before transferSettings is called (instead of relying on a point-in-time cache read).
  const settingsQueries = useQueries({
    queries: (config.loadSettings || []).map(entry => {
      const key = getSettingsKey(entry)
      return { queryKey: [key], queryFn: () => getMe(key), staleTime: Infinity }
    })
  })
  const settingsReady = settingsQueries.length === 0 || settingsQueries.every(q => q.isSuccess)

  let result = {}

  if (load) {
    let queryKey
    if (slug) queryKey = itemId !== undefined ? [`${endpoint}`, `${itemId}`, `${slug}`] : [`${endpoint}`, `${slug}`];
    else queryKey = itemId !== undefined ? [`${endpoint}`, `${itemId}`] : [`${endpoint}`];
    //console.log('[PageView] queryKey: ', queryKey)
    result = useQuery({
      queryKey: queryKey,
      queryFn: () => getMe(`${endpoint}${itemId ? `/${itemId}` : ''}`),
      staleTime: staleTime
    });
  } else if (!load) {
    if (result) {
      let data = {
        data: { data: { state: 'draft', ...(options.defaultValues || {}), ...(location.state || {}) } },
        isSuccess: true
      }
      result = { ...data }
    }
  }

  // Force-hide mobile nav while this PageView is mounted (pre-paint to avoid flicker)
  useLayoutEffect(() => {
    const prev = mobileNavBarState.visible;
    hideMobileNav();
    return () => {
      if (prev) {
        showMobileNav();
      } else {
        hideMobileNav();
      }
    };
  }, [])


  //Initilize the state on successfull load.
  useEffect(
    () => {
      if (!result.data) return
      if (!settingsReady) return

      const dataUpdatedAt = result.dataUpdatedAt
      // For load:false routes, result is a synthetic object with no dataUpdatedAt.
      // In that case we treat it as "always new" so the !loaded guard is sufficient.
      // For load:true routes, isNewData uses the dataUpdatedAt timestamp (a number,
      // not an object reference) so it reliably changes whenever React Query fetches
      // fresh data from the server, even when the response content is identical.
      const isNewData = dataUpdatedAt === undefined
        ? true  // synthetic result (load:false) — always treat as new
        : lastProcessedRef.current !== dataUpdatedAt

      const shouldProcess = dataUpdatedAt === undefined ? !loaded : isNewData

      if (shouldProcess) {
        if (dataUpdatedAt !== undefined) {
          lastProcessedRef.current = dataUpdatedAt
        }

        let enrichedData
        if (config?.loadSettings !== undefined) {
          enrichedData = transferSettings(result.data.data, getSettingsData, config.loadSettings)
          setState({ ...enrichedData, loaded: true, entity: config.entity })
        } else {
          setState({ ...result.data.data, loaded: true, entity: config.entity })
        }
      }
    }
    , [result.data, result.dataUpdatedAt, loaded, dynamicReload, settingsReady]
  )
  
  // Refetch when loaded becomes false (triggered by child components wanting fresh data)
  useEffect(() => {
    if (loaded === false && result.refetch) {
      result.refetch();
    }
  }, [loaded])

  // Here we wait for settings to finish loading if it exists
  if (result.isLoading) { return <Loader /> }

  //Here we wait for the Record to finish Loading.
  if (result.isError) { return <span>Error: {result.data.message}</span> }


  if (result.isSuccess && loaded) {
    //console.log('Rendering PageView')
    return (
      <>
        {config.transition?.active ? <TransitionLayer type={type} options={options} dispatch={dispatch} config={config} />
          : <PageLayout type={type} options={options} dispatch={dispatch} />}
      </>
    );

  }
}

export default PageView
